import React, {useState, useRef, useEffect, useCallback} from 'react';
import {
    Box,
    Button,
    Card,
    TextField,
    Typography,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select, MenuItem, Alert, Dialog, DialogTitle, DialogContent, CircularProgress, DialogActions, Tooltip,
} from '@mui/material';
import {ArrowBack, Delete, Edit, ChevronLeft, ChevronRight, GpsFixed, Warning} from '@mui/icons-material';
import {makeStyles} from '@mui/styles';
import {formatDate, getReadableFileSizeString} from "../utils/utils";
import Cookies from 'universal-cookie';
import {useAuth} from '../utils/useAuth';
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '30px',
    },
    panelsRow: {
        display: 'flex',
        gap: '30px',
    },
    hotspotsBottomSection: {
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-start',
    },
    leftPanel: {
        flex: '0 0 200px',
    },
    centerPanel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    },
    rightPanel: {
        flex: '0 0 320px',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        color: '#666',
        cursor: 'pointer',
        marginBottom: '20px',
        '&:hover': {
            color: '#000',
        },
    },
    thumbnail: {
        width: '100%',
        borderRadius: '8px',
        marginBottom: '15px',
        cursor: 'pointer',
        border: '2px solid #e0e0e0',
        '&:hover': {
            borderColor: '#999',
        },
    },
    imageInfoLabel: {
        fontSize: '12px',
        color: '#666',
        marginTop: '12px',
        marginBottom: '4px',
        fontWeight: 'bold',
    },
    imageInfoValue: {
        fontSize: '14px',
    },
    updateButton: {
        backgroundColor: '#000000 !important',
        color: 'white !important',
        fontFamily: "'GucciSans-Bold' !important",
        marginTop: '15px',
        '&:hover': {
            backgroundColor: '#0052cc !important',
        },
    },
    imageName: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        marginBottom: '20px',
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '1rem',
        fontSize: '12px',
        fontWeight: 'bold',
    },
    configuredBadge: {
        backgroundColor: '#e8f5e9',
        color: '#2e7d32',
    },
    notConfiguredBadge: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
    },
    imageContainer: {
        position: 'relative',
        display: 'inline-block',
        width: '100%',
        marginBottom: '20px',
    },
    mainImage: {
        width: '100%',
        objectFit: 'contain',
        borderRadius: '8px',
        display: 'block',
        backgroundColor: '#f5f5f5',
    },
    hotspot: {
        position: 'absolute',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: '#000000',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: '14px',
        fontWeight: 'bold',
        border: '2px solid white',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        transform: 'translate(-50%, -50%)',
        '&:hover': {
            backgroundColor: '#0052cc',
        },
    },
    navigationBar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    navigationButton: {
        backgroundColor: '#000 !important',
        color: 'white !important',
        padding: '8px 16px !important',
        '&:disabled': {
            backgroundColor: '#ccc !important',
        },
    },
    addHotspotButton: {
        backgroundColor: '#000000 !important',
        color: 'white !important',
        marginBottom: '20px',
    },
    addingHotspotIndicator: {
        textAlign: 'center',
        padding: '12px',
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
        borderRadius: '4px',
        marginBottom: '15px',
        fontSize: '13px',
        fontWeight: 'bold',
    },
    hotspotsTable: {
        marginTop: '0px',
    },
    hotspotsTableCell: {
        padding: '12px !important',
        fontSize: '14px',
    },
    actionButton: {
        padding: '4px !important',
        minWidth: 'auto !important',
    },
    rightPanelTitle: {
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: '16px',
    },
    positionInputs: {
        display: 'flex',
        gap: '12px',
        marginBottom: '16px',
    },
    positionInput: {
        flex: 1,
    },
    positionInputsWithButton: {
        display: 'flex',
        gap: '12px',
        alignItems: 'flex-end',
        marginBottom: '16px',
    },
    updatePositionButton: {
        padding: '12px !important',
        minWidth: 'auto !important',
    },
}));

export default function PhotostudioImageDetail({imagesList, currentImageIndex, onBack, selectedEvent, onDataChanged}) {
    const classes = useStyles();
    const imageRef = useRef(null);
    const {getAccessToken} = useAuth();

    const [localImages, setLocalImages] = useState(imagesList || []);
    const [currentIndex, setCurrentIndex] = useState(currentImageIndex || 0);
    const [addingHotspot, setAddingHotspot] = useState(false);
    const [selectedHotspot, setSelectedHotspot] = useState(null);
    const [editingHotspotPosition, setEditingHotspotPosition] = useState(false);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [hotspotToDelete, setHotspotToDelete] = useState({display: null, displayIndex: null});
    const [newHotspotX, setNewHotspotX] = useState('');
    const [newHotspotY, setNewHotspotY] = useState('');
    const [newDisplayId, setNewDisplayId] = useState('');
    const [editDisplayId, setEditDisplayId] = useState('');
    const [editX, setEditX] = useState('');
    const [editY, setEditY] = useState('');
    const [originalEditX, setOriginalEditX] = useState('');
    const [originalEditY, setOriginalEditY] = useState('');
    const [displays, setDisplays] = useState([]);
    const [state, setState] = React.useState({
        openSuccess: false,
        openError: false,
        vertical: 'top',
        horizontal: 'right',
        successMessage: '',
        errorMessage: ''
    });
    const {vertical, horizontal, openSuccess, successMessage, openError, errorMessage} = state;

    const handleClose = () => {
        setState({...state, openSuccess: false, openError: false});
    };

    const Alert = React.forwardRef(function Alert(props, ref) {
        return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
    });

    const currentImage = localImages[currentIndex];

    function getDisplayId(display) {
        return display?.id || display?.displayID || '';
    }

    function getImageDisplays(image) {
        return Array.isArray(image?.displays) ? image.displays : [];
    }

    function updateCurrentImageDisplays(nextDisplays) {
        setLocalImages(prevImages => prevImages.map((image, index) => (
            index === currentIndex
                ? {...image, displays: nextDisplays}
                : image
        )));
    }

    function getDisplayName(display) {
        return display?.name || display?.displayName || `Display ${getDisplayId(display)}`;
    }

    function getAvailableDisplay(displayId) {
        return displays.find(display => String(getDisplayId(display)) === String(displayId));
    }

    const imageDisplays = getImageDisplays(currentImage);
    const selectedDisplay = Number.isInteger(selectedHotspot) ? imageDisplays[selectedHotspot] : null;

    const fetchDisplays = useCallback(async () => {
        try {
            const token = await getAccessToken();
            if (!token) throw new Error('Not authenticated');
            const cookies = new Cookies();
            const hashCode = cookies.get('hashCode') || '';
            const authHeader = token + hashCode;

            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}displays?eventID=${selectedEvent}`,
                {
                    method: 'GET',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const data = await response.json();
            setDisplays(data || []);
        } catch (error) {
            console.error('Errore fetchDisplays:', error);
            setDisplays([]);
        }
    }, [getAccessToken, selectedEvent]);

    useEffect(() => {
        if (selectedEvent) {
            fetchDisplays();
        }
    }, [fetchDisplays, selectedEvent]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [currentImageIndex]);

    function handleImageClick(e) {
        if (!addingHotspot && !editingHotspotPosition) return;

        const imageRect = imageRef.current.getBoundingClientRect();
        const naturalWidth = imageRef.current.naturalWidth;
        const displayWidth = imageRef.current.clientWidth;
        const scale = naturalWidth / displayWidth;

        const x = Math.round((e.clientX - imageRect.left) * scale);
        const y = Math.round((e.clientY - imageRect.top) * scale);

        if (addingHotspot) {
            setNewHotspotX(x.toString());
            setNewHotspotY(y.toString());
        } else if (editingHotspotPosition) {
            setEditX(x.toString());
            setEditY(y.toString());
        }
    }

    async function getAuthHeader() {
        try {
            const token = await getAccessToken();
            if (!token) throw new Error("Utente non autenticato");
            const cookies = new Cookies();
            const hashCode = cookies.get('hashCode') || '';
            return token + hashCode;
        } catch (err) {
            console.error("Errore getAuthHeader:", err);
            throw err;
        }
    }

    async function handleAddHotspot() {
        if (!newDisplayId) {
            setState({openSuccess: false, openError: true, errorMessage: 'Select a display'});
            return;
        }

        if (!newHotspotX || !newHotspotY) {
            setState({openSuccess: false, openError: true, errorMessage: 'Click on the image to set position'});
            return;
        }

        const display = getAvailableDisplay(newDisplayId);

        const newDisplay = {
            ...(display || {}),
            id: getDisplayId(display) || newDisplayId,
            xPos: parseInt(newHotspotX, 10),
            yPos: parseInt(newHotspotY, 10),
        };

        const hotspot = {
            eventID: selectedEvent,
            displayID: newDisplay.id,
            photoStudioID: currentImage.id,
            xPos: newDisplay.xPos,
            yPos: newDisplay.yPos,
        };

        try {
            const authHeader = await getAuthHeader();

            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}displaymapping`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(hotspot),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();
            await onDataChanged();

            updateCurrentImageDisplays([...imageDisplays, newDisplay]);

            setAddingHotspot(false);
            setSelectedHotspot(imageDisplays.length);
            setEditDisplayId(getDisplayId(newDisplay));
            setEditX(newDisplay.xPos.toString());
            setEditY(newDisplay.yPos.toString());
            setNewDisplayId('');
            setNewHotspotX('');
            setNewHotspotY('');

        } catch (error) {
            setState({openSuccess: false, openError: true, errorMessage: 'Error adding display'});
        }
    }


    async function handleDeleteHotspot(displayIndex, display) {
        try {
            const authHeader = await getAuthHeader();

            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}displaymapping/${display.displayMappingID}`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                }
            );

            if (!response.ok) {
                setOpenDeleteDialog(false)
                throw new Error(`HTTP error: ${response.status}`);
            }
            updateCurrentImageDisplays(imageDisplays.filter((display, index) => index !== displayIndex));
            setHotspotToDelete(null);
            setSelectedHotspot(null);
            setOpenDeleteDialog(false)
        } catch (error) {
            setState({openSuccess: false, openError: true, errorMessage: 'Error deleting display'});
        }
    }

    async function handleUpdateHotspotFromRight() {
        if (selectedHotspot === null || !selectedDisplay) return;

        if (!editDisplayId) {
            setState({
                openSuccess: false,
                openError: true,
                errorMessage: 'Select a display',
            });
            return;
        }

        if (editX === '' || editY === '') {
            setState({
                openSuccess: false,
                openError: true,
                errorMessage: 'Set the hotspot position',
            });
            return;
        }

        const display = imageDisplays[selectedHotspot];
        const selectedAvailableDisplay = getAvailableDisplay(editDisplayId);

        const updatedDisplay = {
            ...display,
            ...(selectedAvailableDisplay || {}),
            id: String(editDisplayId),
            displayMappingID: display.displayMappingID,
            xPos: parseInt(editX, 10),
            yPos: parseInt(editY, 10),
        };


        const hotspot = {
            id: display.displayMappingID,
            eventID: selectedEvent,
            displayID: String(editDisplayId),
            photoStudioID: currentImage.id,
            xPos: updatedDisplay.xPos,
            yPos: updatedDisplay.yPos,
        };


        try {
            const authHeader = await getAuthHeader();

            const response = await fetch(
                `${process.env.REACT_APP_API_ENDPOINT}displaymapping`,
                {
                    method: 'POST',
                    headers: {
                        Authorization: authHeader,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(hotspot),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error: ${response.status}`);
            }

            const data = await response.json();
            await onDataChanged();

            const nextDisplays = imageDisplays.map((item, index) =>
                index === selectedHotspot ? updatedDisplay : item
            );

            updateCurrentImageDisplays(nextDisplays);
            setEditingHotspotPosition(false);

            setState({
                openSuccess: true,
                openError: false,
                successMessage: 'Display updated successfully',
            });

        } catch (error) {
            console.error('Error updating hotspot:', error);

            setState({
                openSuccess: false,
                openError: true,
                errorMessage: 'Error updating display',
            });
        }
    }


    function goToPrevious() {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
            setAddingHotspot(false);
            setSelectedHotspot(null);
        }
    }

    function goToNext() {
        if (currentIndex < localImages.length - 1) {
            setCurrentIndex(currentIndex + 1);
            setAddingHotspot(false);
            setSelectedHotspot(null);
        }
    }

    function getImageDisplayUrl(display) {
        return displays.find(d => d.id === display.id)?.imageLink200 || '';
    }

    function getNewDisplayName(display) {
        return displays.find(d => d.id === display)?.name || '';
    }

    if (!currentImage) {
        return <Box>No image selected</Box>;
    }

    const isConfigured = imageDisplays.length > 0;
    const hasDimensions = currentImage.dimensions?.width != null && currentImage.dimensions?.height != null;
    const hasFileSize = Boolean(currentImage.fileSize);
    const hasUploaded = Boolean(currentImage.uploaded);

    return (
        <Box className={classes.root}>
            <Box className={classes.panelsRow}>
                <Box className={classes.leftPanel}>
                    <Box className={classes.backButton} onClick={onBack}>
                        <ArrowBack sx={{fontSize: '20px'}}/>
                        <Typography sx={{fontSize: '14px'}}>Back to images</Typography>
                    </Box>

                    <Box
                        component="img"
                        src={currentImage.thumbUrl || currentImage.imageUrl}
                        alt={currentImage.name}
                        className={classes.thumbnail}
                    />

                    <Typography className={classes.imageInfoLabel}>File name</Typography>
                    <Typography className={classes.imageInfoValue}>{currentImage.name}</Typography>

                    {hasDimensions && (
                        <>
                            <Typography className={classes.imageInfoLabel}>Dimensions</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Typography className={classes.imageInfoValue}>{currentImage.dimensions.width}
                                    X {currentImage.dimensions.height}</Typography>
                                {(currentImage.dimensions.width !== 3840 || currentImage.dimensions.height !== 2160) && (
                                    <Tooltip title="Image not in size 3840 X 2160" arrow>
                                        <Warning fontSize="small" sx={{ color: '#FFA500', cursor: 'pointer' }} />
                                    </Tooltip>
                                )}
                            </Box>
                        </>
                    )}

                    {hasFileSize && (
                        <>
                            <Typography className={classes.imageInfoLabel}>File size</Typography>
                            <Typography className={classes.imageInfoValue}>
                                {getReadableFileSizeString(currentImage.fileSize)}
                            </Typography>
                        </>
                    )}

                    {hasUploaded && (
                        <>
                            <Typography className={classes.imageInfoLabel}>Uploaded</Typography>
                            <Typography className={classes.imageInfoValue}>{formatDate(currentImage.uploaded)}</Typography>
                        </>
                    )}

                </Box>

                <Box className={classes.centerPanel}>
                    <Box className={classes.imageName}>
                        <Typography sx={{fontSize: '18px', fontWeight: 'bold'}}>
                            {currentImage.name}
                        </Typography>
                        <Box
                            className={`${classes.statusBadge} ${
                                isConfigured ? classes.configuredBadge : classes.notConfiguredBadge
                            }`}
                        >
                            {isConfigured ? 'Configured' : 'Not configured'}
                        </Box>
                        {isConfigured && (
                            <Typography sx={{fontSize: '12px', color: '#666'}}>
                                {imageDisplays.length} {imageDisplays.length === 1 ? 'display' : 'displays'}
                            </Typography>
                        )}
                    </Box>

                    <Box className={classes.navigationBar}>
                        <Button
                            variant="contained"
                            startIcon={<ChevronLeft/>}
                            onClick={goToPrevious}
                            disabled={currentIndex === 0}
                            className={classes.navigationButton}
                        >
                            Previous
                        </Button>
                        <Typography sx={{fontSize: '14px', color: '#666'}}>
                            {currentIndex + 1} of {localImages.length}
                        </Typography>
                        <Button
                            variant="contained"
                            endIcon={<ChevronRight/>}
                            onClick={goToNext}
                            disabled={currentIndex === localImages.length - 1}
                            className={classes.navigationButton}
                        >
                            Next
                        </Button>
                    </Box>

                    <Box
                        className={classes.imageContainer}
                        onClick={handleImageClick}
                    >
                        <Box
                            ref={imageRef}
                            component="img"
                            src={currentImage.imageUrl}
                            alt={currentImage.name}
                            className={classes.mainImage}
                            sx={{
                                cursor: addingHotspot || editingHotspotPosition ? 'crosshair' : 'default'
                            }}
                        />

                        {addingHotspot &&
                            newHotspotX !== '' &&
                            newHotspotY !== '' && (
                                <Box
                                    className={classes.hotspot}
                                    sx={{
                                        left: `${(Number(newHotspotX) / (imageRef.current?.naturalWidth || 1)) * 100}%`,
                                        top: `${(Number(newHotspotY) / (imageRef.current?.naturalHeight || 1)) * 100}%`,
                                        backgroundColor: '#1565c0 !important',
                                    }}
                                >
                                    {imageDisplays.length + 1}
                                </Box>
                            )}

                        {selectedHotspot !== null && !addingHotspot &&
                            editX !== '' &&
                            editY !== '' && (
                                <Box
                                    className={classes.hotspot}
                                    sx={{
                                        left: `${(Number(editX) / (imageRef.current?.naturalWidth || 1)) * 100}%`,
                                        top: `${(Number(editY) / (imageRef.current?.naturalHeight || 1)) * 100}%`,
                                        backgroundColor: '#1565c0 !important',
                                    }}
                                >
                                    {selectedHotspot + 1}
                                </Box>
                            )}

                        {imageDisplays.map((display, index) => {
                            const naturalWidth = imageRef.current?.naturalWidth || 1;
                            const naturalHeight = imageRef.current?.naturalHeight || 1;

                            const xPercent = (display.xPos / naturalWidth) * 100;
                            const yPercent = (display.yPos / naturalHeight) * 100;

                            return selectedHotspot === index ? null : (
                                <Box
                                    key={`${getDisplayId(display)}-${index}`}
                                    className={classes.hotspot}
                                    sx={{
                                        left: `${xPercent}%`,
                                        top: `${yPercent}%`,
                                        backgroundColor: '#000000',
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setAddingHotspot(false);
                                        setSelectedHotspot(index);
                                        setEditDisplayId(getDisplayId(display));
                                        const xStr = (display.xPos !== undefined && display.xPos !== null ? display.xPos : '').toString();
                                        const yStr = (display.yPos !== undefined && display.yPos !== null ? display.yPos : '').toString();
                                        setEditX(xStr);
                                        setEditY(yStr);
                                        setOriginalEditX(xStr);
                                        setOriginalEditY(yStr);
                                    }}
                                >
                                    {index + 1}
                                </Box>
                            );
                        })}
                    </Box>

                    {addingHotspot && (
                        <Box className={classes.addingHotspotIndicator}>
                            Click on the image to set position: ({newHotspotX || '?'}, {newHotspotY || '?'})
                        </Box>
                    )}

                    {editingHotspotPosition && (
                        <Box className={classes.addingHotspotIndicator}>
                            Click on the image to update position: ({editX || '?'}, {editY || '?'})
                        </Box>
                    )}
                </Box>


                <Box className={classes.rightPanel}>
                    {addingHotspot && (
                        <>
                            <Typography className={classes.rightPanelTitle}>
                                Create Display Binding
                            </Typography>
                            {newDisplayId &&
                                <Box>
                                    <img src={getImageDisplayUrl(newDisplayId)} alt={getNewDisplayName(newDisplayId)}/>
                                </Box>
                            }

                            <Typography
                                sx={{fontSize: '12px', fontWeight: 'bold', marginBottom: '8px'}}>Display</Typography>
                            <Select
                                fullWidth
                                size="small"
                                className="gucci_text select_filter"
                                value={newDisplayId}
                                onChange={(e) => setNewDisplayId(e.target.value)}
                                sx={{marginBottom: '16px'}}
                                displayEmpty
                            >
                                <MenuItem value="">Select display</MenuItem>
                                {displays.map((display) => (
                                    <MenuItem key={`create-display-${getDisplayId(display)}`}
                                              value={getDisplayId(display)}>
                                        {getDisplayName(display)}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Typography
                                sx={{fontSize: '12px', fontWeight: 'bold', marginBottom: '8px'}}>Position</Typography>
                            <Box className={classes.positionInputs}>
                                <TextField
                                    label="X"
                                    type="number"
                                    size="small"
                                    value={newHotspotX}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const max = currentImage.dimensions?.width;

                                        if (value === '') {
                                            setNewHotspotX('');
                                            return;
                                        }

                                        const number = Number(value);

                                        if (
                                            !Number.isNaN(number) &&
                                            number >= 0 &&
                                            (max == null || number <= max)
                                        ) {
                                            setNewHotspotX(value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (['e', 'E', '+', '-'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className={classes.positionInput}
                                    inputProps={{
                                        min: 0,
                                        max: currentImage.dimensions?.width,
                                    }}
                                />

                                <TextField
                                    label="Y"
                                    type="number"
                                    size="small"
                                    value={newHotspotY}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const max = currentImage.dimensions?.height;

                                        if (value === '') {
                                            setNewHotspotY('');
                                            return;
                                        }

                                        const number = Number(value);

                                        if (
                                            !Number.isNaN(number) &&
                                            number >= 0 &&
                                            (max == null || number <= max)
                                        ) {
                                            setNewHotspotY(value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (['e', 'E', '+', '-'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className={classes.positionInput}
                                    inputProps={{
                                        min: 0,
                                        max: currentImage.dimensions?.height,
                                    }}
                                />
                            </Box>

                            <Button
                                fullWidth
                                variant="contained"
                                className={classes.updateButton}
                                onClick={handleAddHotspot}
                            >
                                Save Display
                            </Button>
                            <Button
                                fullWidth
                                sx={{color: '#999', marginTop: '8px'}}
                                onClick={() => {
                                    setAddingHotspot(false);
                                    setNewDisplayId('');
                                    setNewHotspotX('');
                                    setNewHotspotY('');
                                }}
                            >
                                Cancel
                            </Button>
                        </>
                    )}
                    {(!addingHotspot &&
                        <Button
                            variant="contained"
                            onClick={() => {
                                setAddingHotspot(!addingHotspot);
                                setNewDisplayId('');
                                setNewHotspotX('');
                                setNewHotspotY('');
                                setSelectedHotspot(null);
                            }}
                            sx={{
                                color: 'white',
                                padding: '8px 16px',
                                display: 'flex',
                                justifySelf: 'flex-end',
                                marginBottom: '1rem'
                            }}
                        >
                            Add
                        </Button>
                    )}
                    {!addingHotspot && selectedDisplay && (
                        <>
                            <Typography className={classes.rightPanelTitle}>
                                Display Details
                                #{selectedHotspot + 1}
                            </Typography>

                            <Box>
                                <img src={getImageDisplayUrl(selectedDisplay)} alt={selectedDisplay.name}/>
                            </Box>

                            <Typography
                                sx={{fontSize: '12px', fontWeight: 'bold', marginBottom: '8px'}}>Display</Typography>
                            <Select
                                fullWidth
                                size="small"
                                className="gucci_text select_filter"
                                value={editDisplayId}
                                onChange={(e) => setEditDisplayId(e.target.value)}
                                sx={{marginBottom: '16px'}}
                                disabled={editingHotspotPosition}
                            >
                                {displays.map((display) => (
                                    <MenuItem key={`detail-display-${getDisplayId(display)}`}
                                              value={getDisplayId(display)}>
                                        {getDisplayName(display)}
                                    </MenuItem>
                                ))}
                            </Select>

                            <Typography
                                sx={{fontSize: '12px', fontWeight: 'bold', marginBottom: '8px'}}>Position</Typography>
                            <Box className={classes.positionInputsWithButton}>
                                <TextField
                                    label="X"
                                    type="number"
                                    size="small"
                                    value={editX}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const max = currentImage.dimensions?.width;

                                        if (value === '') {
                                            setEditX('');
                                            return;
                                        }

                                        const number = Number(value);

                                        if (
                                            !Number.isNaN(number) &&
                                            number >= 0 &&
                                            (max == null || number <= max)
                                        ) {
                                            setEditX(value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (['e', 'E', '+', '-'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className={classes.positionInput}
                                    inputProps={{
                                        min: 0,
                                        max: currentImage.dimensions?.width,
                                    }}
                                />
                                <TextField
                                    label="Y"
                                    type="number"
                                    size="small"
                                    value={editY}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        const max = currentImage.dimensions?.height;

                                        if (value === '') {
                                            setEditY('');
                                            return;
                                        }

                                        const number = Number(value);

                                        if (
                                            !Number.isNaN(number) &&
                                            number >= 0 &&
                                            (max == null || number <= max)
                                        ) {
                                            setEditY(value);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (['e', 'E', '+', '-'].includes(e.key)) {
                                            e.preventDefault();
                                        }
                                    }}
                                    className={classes.positionInput}
                                    inputProps={{
                                        min: 0,
                                        max: currentImage.dimensions?.height,
                                    }}
                                />
                                {!editingHotspotPosition && (
                                    <IconButton
                                        onClick={() => setEditingHotspotPosition(true)}
                                        className={classes.updatePositionButton}
                                        sx={{backgroundColor: '#000000', color: 'white', '&:hover': {backgroundColor: '#333333'}}}
                                        title="Click on image to update position"
                                    >
                                        <GpsFixed fontSize="small" />
                                    </IconButton>
                                )}
                            </Box>

                            {editingHotspotPosition && (
                                <>
                                    <Button
                                        fullWidth
                                        variant="contained"
                                        className={classes.updateButton}
                                        sx={{marginBottom: '8px'}}
                                        onClick={handleUpdateHotspotFromRight}
                                    >
                                        Save Position
                                    </Button>
                                    <Button
                                        fullWidth
                                        sx={{color: '#999'}}
                                        onClick={() => {
                                            setEditX(originalEditX);
                                            setEditY(originalEditY);
                                            setEditingHotspotPosition(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                </>
                            )}

                            {!editingHotspotPosition && (
                                <Button
                                    fullWidth
                                    variant="contained"
                                    className={classes.updateButton}
                                    onClick={handleUpdateHotspotFromRight}
                                >
                                    Update Display
                                </Button>
                            )}
                        </>
                    )}
                </Box>
            </Box>
            <Box sx={{width: '100%'}}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px'
                    }}>
                    <Typography sx={{fontSize: '16px', fontWeight: 'bold'}}>
                        Displays ({imageDisplays.length})
                    </Typography>
                </Box>
                <TableContainer component={Card} className={classes.hotspotsTable}>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{backgroundColor: '#f5f5f5'}}>
                                <TableCell className={classes.hotspotsTableCell}>#</TableCell>
                                <TableCell className={classes.hotspotsTableCell}>Display</TableCell>
                                <TableCell className={classes.hotspotsTableCell}>Position (X, Y)</TableCell>
                                <TableCell className={classes.hotspotsTableCell} width={80}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {imageDisplays.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center" sx={{padding: '20px', color: '#999'}}>
                                        No displays yet. Click "+ Add" to create one.
                                    </TableCell>
                                </TableRow>
                            )}

                            {imageDisplays.map((display, index) => {
                                return (
                                    <TableRow
                                        key={`${getDisplayId(display)}-${index}`}
                                        sx={{
                                            backgroundColor: selectedHotspot === index ? '#fff3e0' : 'transparent',
                                            cursor: 'pointer',
                                        }}
                                        onClick={() => {
                                            setAddingHotspot(false);
                                            setSelectedHotspot(index);
                                            setEditDisplayId(getDisplayId(display));
                                            setEditX((display.xPos || '').toString());
                                            setEditY((display.yPos || '').toString());
                                        }}
                                    >
                                        <TableCell className={classes.hotspotsTableCell}>{index + 1}</TableCell>
                                        <TableCell
                                            className={classes.hotspotsTableCell}>{getDisplayName(display)}</TableCell>
                                        <TableCell className={classes.hotspotsTableCell}>
                                            ({display.xPos}, {display.yPos})
                                        </TableCell>
                                        <TableCell className={classes.hotspotsTableCell}>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAddingHotspot(false);
                                                    setSelectedHotspot(index);
                                                    setEditDisplayId(getDisplayId(display));
                                                    const xStr = (display.xPos !== undefined && display.xPos !== null ? display.xPos : '').toString();
                                                    const yStr = (display.yPos !== undefined && display.yPos !== null ? display.yPos : '').toString();
                                                    setEditX(xStr);
                                                    setEditY(yStr);
                                                    setOriginalEditX(xStr);
                                                    setOriginalEditY(yStr);
                                                    setEditingHotspotPosition(true);
                                                }}
                                                className={classes.actionButton}
                                            >
                                                <Edit sx={{fontSize: '18px', color: '#666'}}/>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenDeleteDialog(true)
                                                    setHotspotToDelete({display, displayIndex: index})

                                                }}
                                                className={classes.actionButton}
                                            >
                                                <Delete sx={{fontSize: '18px', color: '#ff6b6b'}}/>
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={openSuccess}
                autoHideDuration={3000}
                onClose={handleClose}
                message="I love snacks"
                key={vertical + horizontal}
            >
                <Alert onClose={handleClose} severity="success" dx={{width: '300px'}}>
                    {successMessage}
                </Alert>
            </Snackbar>

            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={openError}
                onClose={handleClose}
                autoHideDuration={3000}
                message=""
                key={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <Alert onClose={handleClose} severity="error" dx={{width: '300px'}}>
                    {errorMessage}
                </Alert>
            </Snackbar>

            <Dialog
                open={openDeleteDialog}
                onClose={() => setOpenDeleteDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                fullWidth
                maxWidth="sm"
            >

                <DialogTitle id="alert-dialog-title">
                    <Typography className="gucci_text_bold" variant="h5">
                        Delete
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <div className='gucci_text'> Do you want delete the display link?</div>
                </DialogContent>
                <DialogActions>
                    <div className="flex">

                        <Button onClick={() => handleDeleteHotspot(hotspotToDelete.displayIndex, hotspotToDelete.display)} className="template_button red"
                                style={{marginRight: "10px"}}>
                            Confirm
                        </Button>

                        <Button onClick={() => setOpenDeleteDialog(false)} className="template_button">
                            Close
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
